/**
 * TemplateLoadingScreen
 *
 * Loading skeleton para los 3 paths de Index.tsx (AR dual home, intl dual
 * home, BaseTemplate default) mientras siteSettings + portfolio cargan.
 *
 * Diseño: dark canvas + brand wordmark con pulse + shimmer bar + status text
 * con fade. Stagger fade-in para que aparezca progresivamente y no salte.
 * Las animaciones viven en una <style> inline para no inflar el CSS global
 * con keyframes que solo usa este componente.
 *
 * Por qué: el loading anterior era una barra simple con keyframes `loadBar`
 * que solo hacía width 0→60%→0%. Visualmente abrupto, sin feedback de marca,
 * sin mensaje de espera. Este reemplazo da identidad visual + feedback de
 * progreso percibido sin agregar peso significativo (~3KB CSS inline).
 */

import { useTranslation } from "react-i18next";
import { BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import type { FC } from "react";

interface TemplateLoadingScreenProps {
  /**
   * Override del brand name. Por default usa BRAND_PERSON_NAME → BRAND_NAME
   * del env. Útil si el caller tiene un name custom (ej. variante de marca).
   */
  brandName?: string;
  /**
   * Mensaje debajo de la barra (i18n key o texto). Default: "Cargando…".
   * Vacío ("") → oculta el mensaje.
   */
  message?: string;
  /**
   * Color de acento de la shimmer bar y el highlight del brand pulse.
   * Default: amber (consistente con VIP CTA). "white" para neutral.
   */
  accent?: "amber" | "white";
  /** Opcional: data-testid para E2E (redirect gate, etc.). */
  "data-testid"?: string;
  /**
   * Cuando true, anima opacity 1→0 en 280ms (cubic-bezier Material easing).
   * El parent que mantiene este componente montado durante el fade debe
   * unmountearlo después de 280ms para limpiar el DOM. pointer-events:none
   * durante fade-out evita clicks accidentales que bloqueen el content
   * debajo.
   */
  fadingOut?: boolean;
}

const ACCENT_COLORS = {
  amber: {
    bar: "linear-gradient(90deg, transparent 0%, rgba(251,191,36,0) 20%, rgba(251,191,36,0.85) 50%, rgba(251,191,36,0) 80%, transparent 100%)",
    glow: "0 0 24px rgba(251,191,36,0.18)",
    dot: "rgb(251,191,36)",
  },
  white: {
    bar: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0) 80%, transparent 100%)",
    glow: "0 0 24px rgba(255,255,255,0.18)",
    dot: "rgb(255,255,255)",
  },
} as const;

export const TemplateLoadingScreen: FC<TemplateLoadingScreenProps> = ({
  brandName: brandOverride,
  message,
  accent = "amber",
  fadingOut = false,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation();
  const brandName = (brandOverride ?? BRAND_PERSON_NAME ?? BRAND_NAME ?? "").trim();
  const statusMessage =
    message === ""
      ? null
      : (message ?? t("loading.default", { defaultValue: "Cargando…" }));
  const colors = ACCENT_COLORS[accent];

  return (
    <div
      className="fixed inset-0 z-[9998] flex flex-col items-center justify-center bg-[#0a0a0a]"
      role="status"
      aria-live="polite"
      aria-label={statusMessage ?? "Cargando"}
      data-testid={dataTestId}
      style={{
        backgroundImage:
          "radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0) 60%)",
        // Fade-out smooth — el parent mantiene el componente montado durante
        // FADE_OUT_MS y renderiza el content debajo en paralelo para que el
        // visitor vea el sitio asomar suave.
        opacity: fadingOut ? 0 : 1,
        transition: "opacity 280ms cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: fadingOut ? "none" : "auto",
        // Safe-area + mobile-first como el splash de App.tsx
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        minHeight: "100svh",
      }}
    >
      <div className="flex flex-col items-center gap-7 px-6">
        {brandName && (
          <span
            className="tls-brand text-white text-[26px] sm:text-[28px] font-display font-semibold tracking-[0.18em] uppercase select-none"
            style={{ textShadow: colors.glow }}
          >
            {brandName}
          </span>
        )}

        {/* Shimmer bar: gradient barrido continuo. El track es el rounded
            outer; el inner gradient se mueve via translateX, lo que da efecto
            de "luz pasando" más fluido que width 0→60%→0% del approach viejo. */}
        <div
          className="tls-track relative w-32 sm:w-36 h-[2px] overflow-hidden rounded-full"
          aria-hidden
        >
          <div
            className="tls-bar absolute inset-y-0 left-0 w-full"
            style={{ backgroundImage: colors.bar }}
          />
        </div>

        {statusMessage && (
          <div className="tls-status flex items-center gap-2 mt-1">
            <span
              className="tls-dot inline-block w-1 h-1 rounded-full"
              style={{ backgroundColor: colors.dot }}
              aria-hidden
            />
            <span className="text-[13px] tracking-normal text-white/55">
              {statusMessage}
            </span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes tlsBrandPulse {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.012); }
        }
        @keyframes tlsBarSweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes tlsDotPulse {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%      { opacity: 1; transform: scale(1.15); }
        }
        @keyframes tlsFadeUp {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .tls-brand {
          animation:
            tlsFadeUp 420ms cubic-bezier(0.22, 0.61, 0.36, 1) both,
            tlsBrandPulse 2.4s ease-in-out 420ms infinite;
        }
        .tls-track {
          background-color: rgba(255, 255, 255, 0.08);
          animation: tlsFadeUp 480ms cubic-bezier(0.22, 0.61, 0.36, 1) 80ms both;
        }
        .tls-bar {
          animation: tlsBarSweep 1.6s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
          will-change: transform;
        }
        .tls-status {
          animation:
            tlsFadeUp 520ms cubic-bezier(0.22, 0.61, 0.36, 1) 200ms both;
        }
        .tls-dot {
          animation: tlsDotPulse 1.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .tls-brand, .tls-bar, .tls-dot, .tls-status, .tls-track {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};
